import { Injectable } from "@angular/core";
import {
    ESG,
    ESGClient,
    GetESGListResponse,
    GetESGListRequest,
    GetESGResponse,
    ESGScopeDataType,
    UpsertESGRequest,
    UpsertESGResponse,
    GetESGMatrixResponse,
    GetESGChartResponse,
} from "../WebApiClient";
import {
    Observable,
    BehaviorSubject,
    combineLatest,
    merge,
    of,
} from "rxjs";
import { SortEvent } from "../models/ngSortable";
import {
    distinctUntilChanged,
    map,
    publishReplay,
    refCount,
    switchMap,
} from "rxjs/operators";
import { Guid } from "../models/Guid";

@Injectable()
export class ESGService {
    private readonly trigger = new BehaviorSubject(true);
    //private readonly ownSubscription: Subscription;
    private _esgs: ESG[] | undefined = [];

    //public readonly esgsObservable: Observable<ESG_ESG[] | undefined>;

    public constructor(private readonly esgClient: ESGClient) {}

    public getESGListObservable(
        pageObservable: Observable<{ page: number; sort: SortEvent }>
    ): Observable<GetESGListResponse | undefined> {
        let o = combineLatest(
            pageObservable.pipe(distinctUntilChanged()),
            this.trigger
        ).pipe(
            map((x) => ({ shouldResolve: x[1], request: x[0] })),
            switchMap((x) =>
                x.shouldResolve
                    ? merge(
                          of(undefined),
                          this.esgClient.getESGList(
                              new GetESGListRequest({
                                  page: x.request.page,
                                  sortColumn: x.request.sort.column,
                                  sortDirection: x.request.sort.direction,
                              })
                          )
                      )
                    : of(undefined)
            ),
            publishReplay(2),
            refCount()
        );

        return o;
    }

    public getESG(id: string): Observable<ESG | undefined> {
        let o = this.esgClient.getESG(id);
        return o;
    }

    public async getMatrix(): Promise<GetESGMatrixResponse | undefined> {
        let o = this.esgClient.getESGMatrix().toPromise();
        return o;
    }

    public async getChartData(): Promise<GetESGChartResponse | undefined> {
        let o = this.esgClient.getESGChart().toPromise();
        return o;
    }

    public async upsert(
        esg: UpsertESGRequest
    ): Promise<UpsertESGResponse> {
        let response = await this.esgClient.upsertESG(esg).toPromise();
        this.trigger.next(true);
        return response;
    }

    public async delete(id: string): Promise<boolean> {
        let response = await this.esgClient.delete(id).toPromise();
        this.trigger.next(true);
        return response;
    }

    public newESG(): GetESGResponse {
        let esg: GetESGResponse = new GetESGResponse({
            id: Guid.newGuid(),
            year: 0,
            scopeList: [
                this.generateScope(1),
                this.generateScope(2),
                this.generateScope(3),
                this.generateScope(4),
            ],
        });

        return esg;
    }

    private generateScope(number: number): any {
        return {
            id: Guid.newGuid(),
            number: number,
            scopeDataList: [
                {
                    esgScopeDataType: ESGScopeDataType.EnergyProcesses,
                    value: 0,
                    id: Guid.newGuid(),
                },
                {
                    esgScopeDataType: ESGScopeDataType.Procurement,
                    value: 0,
                    id: Guid.newGuid(),
                },
                {
                    esgScopeDataType: ESGScopeDataType.SoldProducts,
                    value: 0,
                    id: Guid.newGuid(),
                },
                {
                    esgScopeDataType: ESGScopeDataType.Transport,
                    value: 0,
                    id: Guid.newGuid(),
                },
                {
                    esgScopeDataType: ESGScopeDataType.WasteRecycling,
                    value: 0,
                    id: Guid.newGuid(),
                },
            ],
        };
    }
}
